import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';

const FullGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const headers = { 'accept-language': language };
        const data = await get('/gallery', headers); // Bez limitu
        setImages(data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst galerii' : 'Failed to load gallery data');
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();

    const initFancybox = () => {
      if (typeof window !== 'undefined' && window.Fancybox) {
        window.Fancybox.bind('[data-fancybox="gallery"]', {
          Thumbs: {
            autoStart: true,
          },
        });
      }
    };

    setTimeout(initFancybox, 500);

    return () => {
      if (typeof window !== 'undefined' && window.Fancybox) {
        window.Fancybox.close();
      }
    };
  }, [language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Celá galerie' : 'Full Gallery'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Celá galerie' : 'Full Gallery'} />
        <div className="text-white text-center py-20">{error}</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={language === 'cs' ? 'Celá galerie' : 'Full Gallery'} />
      <section className="py-20 bg-dark-gray" ref={sectionRef}>
        <div className="container mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => {
              const imageData = image.translations && image.translations[language]
                ? image.translations[language]
                : (image.translations && image.translations.en
                  ? image.translations.en
                  : { title: '', alt_text: '' });
              return (
                <a
                  key={image.id}
                  href={import.meta.env.DEV ? image.image_path : `${IMAGE_BASE_URL}${image.image_path}`}
                  data-fancybox="gallery"
                  className={`block overflow-hidden rounded-lg group ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="relative h-52 overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src={import.meta.env.DEV 
                          ? (image.thumbnail_path || image.image_path)
                          : (image.thumbnail_path ? `${IMAGE_BASE_URL}${image.thumbnail_path}` : `${IMAGE_BASE_URL}${image.image_path}`)}
                        alt={imageData.alt_text || imageData.title || 'Gallery Image'}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/images/gallery/placeholder.jpg';
                        }}
                      />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default FullGallery;